import React, { useRef, useState } from "react";
import { Box, Popover, Typography } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

/**
 * Page header filter row.
 *
 * Pages kept growing filters until the row wrapped under the sticky header and
 * clipped the floating labels. FilterBar keeps the row to a single line: the
 * filters marked `primary` stay inline and everything else collapses behind a
 * "More filters" popover.
 *
 * filters: [{ key, node, primary, trailing }]
 *   key      — stable identity for the list
 *   node     — the filter control itself, rendered as-is so each keeps its own
 *              label, value and behaviour
 *   primary  — true to stay inline; anything else moves into the popover. When the
 *              bar first renders with no primary filter, every filter stays inline
 *              and there is no popover. That layout is fixed for the life of the
 *              bar, so render it only once the page knows its filters.
 *   trailing — true to stay inline but sit after the More filters trigger. For
 *              the action buttons that belong at the end of the row rather than
 *              among the filters, such as the Smart Alerts settings gear.
 *
 * Falsy entries are ignored, so callers can inline their own conditions:
 *   filters={[ showTimePeriod && { key: "timePeriod", node: <X /> }, ... ]}
 */

// Matches FilterSelectBox: same height, radius, border and indigo active state,
// minus the floating label, since this trigger holds no value of its own.
const triggerStyles = (open) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  border: "1px solid transparent",
  borderColor: open ? "#6D72F6" : "#DEDEDE",
  borderRadius: "4px",
  px: 1.5,
  py: 0.75,
  minWidth: 150,
  cursor: "pointer",
  position: "relative",
  "&:hover": { borderColor: "#6D72F6" },
});

const FilterBar = ({ filters = [], moreLabel = "More filters", testId = "filter-bar" }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const visibleFilters = filters.filter(Boolean).filter((filter) => filter.node);
  const trailingFilters = visibleFilters.filter((filter) => filter.trailing);
  const markedPrimary = visibleFilters.filter(
    (filter) => filter.primary && !filter.trailing
  );
  const nonPrimary = visibleFilters.filter(
    (filter) => !filter.primary && !filter.trailing
  );
  // With nothing marked primary there is nothing to keep the row short for, and a
  // "More filters" trigger standing alone just hides every filter behind a click.
  // That happens when an org turns the primary filter off in org meta (Battery
  // Intelligence with globalFilters.location false), so show the rest inline.
  //
  // Decided once, on the first render that has filters, and never revisited.
  // Smart Alerts, BMS Faults and Fleet Performance render the bar before org meta
  // arrives, when every filter still counts as visible, so their primary filter
  // can vanish a render later. Switching to inline then would move the overflow
  // filters out of the popover into the row - a new parent, so React remounts
  // them, and their mount effects refetch options and re-seed page filter state
  // (the keepMounted note below). A bar that first renders with no primary
  // filter, as Battery Intelligence does, gets the inline layout.
  const allInlineRef = useRef(null);
  if (allInlineRef.current === null && visibleFilters.length > 0) {
    allInlineRef.current = markedPrimary.length === 0;
  }
  const allInline = allInlineRef.current === true;
  const primaryFilters = allInline ? nonPrimary : markedPrimary;
  const overflowFilters = allInline ? [] : nonPrimary;

  if (visibleFilters.length === 0) return null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "nowrap" }}>
      {primaryFilters.map((filter) => (
        <Box key={filter.key} sx={{ flexShrink: 0 }}>
          {filter.node}
        </Box>
      ))}

      {overflowFilters.length > 0 && (
        <>
          <Box
            onClick={(e) => setAnchorEl(e.currentTarget)}
            role="button"
            data-testid={`${testId}-more`}
            sx={{ ...triggerStyles(open), flexShrink: 0 }}
          >
            <Typography
              variant="fw500s12lh24cA2A2A2"
              sx={{
                color: "#2B2B2B",
                lineHeight: "24px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                minWidth: 0,
              }}
            >
              {moreLabel}
            </Typography>
            <ArrowDropDownIcon
              fontSize="large"
              sx={{
                color: open ? "#6D72F6" : "#3D4B5A",
                transform: open ? "rotate(180deg)" : "",
                flexShrink: 0,
              }}
            />
          </Box>

          {/* keepMounted, because a Popover unmounts its children when closed and
              these are stateful filters, not static markup. Without it every open
              remounts them: mount effects refetch their options, and a control that
              seeds the page's filter state on load (OperationalStatusMultiSelect's
              onOptionsLoaded, documented as "once on mount") fires again, resetting
              the user's selection to the default and re-running every data effect
              keyed on it. Before this component they were rendered inline and
              mounted once with the page; keepMounted restores that lifecycle. */}
          <Popover
            keepMounted
            open={open}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: "10px",
                  backgroundColor: "#FFFFFF",
                  boxShadow:
                    "2px 2px 4px 0px rgba(151, 151, 151, 0.25), -2px -2px 4px 0px rgba(151, 151, 151, 0.25)",
                  mt: 1,
                },
              },
            }}
          >
            {/* Each control keeps its own floating label, so the popover only
                needs to stack them with enough room for those labels. */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
                px: 2,
                pt: 2.5,
                pb: 2,
                minWidth: 220,
              }}
              data-testid={`${testId}-more-panel`}
            >
              {overflowFilters.map((filter) => (
                <Box key={filter.key}>{filter.node}</Box>
              ))}
            </Box>
          </Popover>
        </>
      )}

      {trailingFilters.map((filter) => (
        <Box key={filter.key} sx={{ flexShrink: 0 }}>
          {filter.node}
        </Box>
      ))}
    </Box>
  );
};

export default FilterBar;
